import { useCallback, useEffect, useMemo, useRef } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { type metrics } from "@north-beam/nb-common";

import {
  useCustomOnConfirmSwitchMetricModal,
  useHasSubmittedMetric,
  useSelectedIsDirtyMetric,
} from "./atoms";
import { SwitchMeticConfirmModal } from "./confirm-close-modal";
import { useCurrentlyUpdatingMetric } from "./hooks/use-currently-updating-metric";
import { useCustomMetrics } from "./hooks/use-custom-metrics";
import { useFormulaHistory } from "./hooks/use-formula-history";

export const CustomMetricList = () => {
  const {
    customMetrics,
    selectedMetric,
    selectedMetricId,
    setSelectedMetric,
    clearSelectedMetric,
  } = useCustomMetrics();

  const { isDirty, currentlyUpdatingMetric, createCurrentlyUpdatingMetric } =
    useCurrentlyUpdatingMetric();
  const [, setIsDirty] = useSelectedIsDirtyMetric();

  const [, setOnConfirmModal] = useCustomOnConfirmSwitchMetricModal();
  const [, setHasSubmitted] = useHasSubmittedMetric();

  const { clearHistory } = useFormulaHistory();
  const updateCurrentSelectedMetricId = (id: number) => {
    if (id === selectedMetricId) {
      return;
    }
    setSelectedMetric(id);
    clearHistory();
  };

  const duplicateMetric = (customMetric: metrics.CustomMetric) => {
    const newMetric = {
      ...customMetric,
      isTemplate: false,
      id: undefined,
      name: `${customMetric.name} (copy)`,
    };
    createCurrentlyUpdatingMetric(newMetric);
    clearHistory();

    clearSelectedMetric();
    setIsDirty(true);
  };

  const templateCustomMetrics = useMemo(() => {
    return customMetrics.filter((c) => c.isTemplate);
  }, [customMetrics]);

  const personalCustomMetrics = useMemo(() => {
    return customMetrics.filter((c) => !c.isTemplate);
  }, [customMetrics]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (currentlyUpdatingMetric && !currentlyUpdatingMetric.id) {
      scrollToBottom();
    }
  }, [currentlyUpdatingMetric, scrollToBottom]);

  return (
    <>
      <div
        className={clsx(
          "h-full w-full border border-gray-2",
          "rounded-md [grid-area:custom-metric-list]",
        )}
      >
        <div className="font-semibold px-3 mt-3 text-sm">MY METRICS</div>
        {customMetrics.length > 0 && (
          <div
            className={clsx(
              "text-sm text-primary",
              "cursor-pointer flex flex-row items-center px-3 py-2",
            )}
            onClick={() => {
              if (isDirty && !currentlyUpdatingMetric?.isTemplate) {
                setOnConfirmModal({
                  metricId: undefined,
                  type: "normal",
                });
                return;
              }
              setHasSubmitted(false);
              createCurrentlyUpdatingMetric();
              clearHistory();
              clearSelectedMetric();
            }}
          >
            <i className="fa-regular fa-plus-circle mr-2" />
            New metric
          </div>
        )}
        <div className="overflow-y-auto h-[15rem]">
          {personalCustomMetrics.map((customMetric) => {
            const isSelected = Boolean(
              selectedMetric && customMetric.id === selectedMetric?.id,
            );

            return (
              <CustomMetricOption
                key={customMetric.id}
                customMetric={customMetric}
                duplicateMetric={() => {
                  if (isDirty && !currentlyUpdatingMetric?.isTemplate) {
                    setOnConfirmModal({
                      metricId: customMetric.id,
                      type: "duplicate",
                    });
                    return;
                  }
                  duplicateMetric(customMetric);
                }}
                isSelected={isSelected}
                onClick={() => {
                  if (
                    selectedMetricId !== customMetric.id &&
                    isDirty &&
                    !currentlyUpdatingMetric?.isTemplate
                  ) {
                    setOnConfirmModal({
                      metricId: customMetric.id,
                      type: "normal",
                    });
                    return;
                  }
                  updateCurrentSelectedMetricId(customMetric.id);
                }}
              />
            );
          })}
          {currentlyUpdatingMetric && !currentlyUpdatingMetric.id && (
            <CustomMetricOption
              key={currentlyUpdatingMetric.id}
              customMetric={currentlyUpdatingMetric}
              duplicateMetric={() => duplicateMetric(currentlyUpdatingMetric)}
              isSelected={true}
              onClick={() => {
                return;
              }}
            />
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="w-full mt-2 mb-3 border-gray-2  border-t" />
        <div className="font-semibold px-3 text-sm">TEMPLATES</div>
        <div className="overflow-y-auto h-[15rem]">
          {templateCustomMetrics.map((customMetric) => {
            const isSelected = Boolean(
              selectedMetric && customMetric.id === selectedMetric?.id,
            );

            return (
              <CustomMetricOption
                key={customMetric.id}
                customMetric={customMetric}
                duplicateMetric={() => {
                  if (isDirty && !currentlyUpdatingMetric?.isTemplate) {
                    setOnConfirmModal({
                      metricId: customMetric.id,
                      type: "duplicate",
                    });
                    return;
                  }
                  duplicateMetric(customMetric);
                }}
                isSelected={isSelected}
                onClick={() => {
                  if (
                    selectedMetricId !== customMetric.id &&
                    isDirty &&
                    !currentlyUpdatingMetric?.isTemplate
                  ) {
                    setOnConfirmModal({
                      metricId: customMetric.id,
                      type: "normal",
                    });
                    return;
                  }
                  updateCurrentSelectedMetricId(customMetric.id);
                }}
              />
            );
          })}
        </div>
      </div>
      <SwitchMeticConfirmModal />
    </>
  );
};

const CustomMetricOption = ({
  customMetric,
  isSelected,
  duplicateMetric,
  onClick,
}: {
  customMetric: metrics.CustomMetric;
  isSelected?: boolean;
  onClick?: () => void;
  duplicateMetric?: () => void;
}) => {
  return (
    <div
      key={customMetric.id}
      className={clsx(
        isSelected &&
          clsx(
            "bg-cosmic-4 text-interstellar+2",
            "shadow-[inset_2px_0px_0px_0px_#7081FF] pointer-events-none",
          ),
        "px-3 text-sm",
        "hover:bg-cosmic-4 hover:text-interstellar+2",
        "hover:shadow-[inset_2px_0px_0px_0px_#7081FF]",
        "hover:cursor-pointer  flex flex-row ",
        "hover:font-bold",
        isSelected && "font-bold",
        {
          "bg-cosmic-4": isSelected,
          "hover:bg-cosmic-3": !isSelected,
        },
        "flex items-center px-3 text-sm relative cursor-pointer",
      )}
      onClick={onClick}
    >
      {isSelected && (
        <div className="h-full w-0.5 bg-cosmic left-0 top-0 absolute" />
      )}
      <div
        className={clsx(
          "flex flex-col py-1 w-full items-start",
          " group/metric relative flex",
          "justify-center",
        )}
      >
        <div className="">{customMetric.name || "New metric"}</div>
        {customMetric.id && (
          <GenericTooltip
            content="Duplicate"
            noInfoCircle
            className="absolute right-0 top-[0.50rem]"
          >
            <i
              className={clsx(
                "group-hover/metric:block hidden",
                "bg-white elevation-1 rounded-full p-1 fa-sharp fa-solid fa-clone",
                "cursor-pointer text-md text-primary font-light",
              )}
              onClick={(event) => {
                event.stopPropagation();
                duplicateMetric?.();
              }}
            ></i>
          </GenericTooltip>
        )}
      </div>
    </div>
  );
};
